import { cn } from '@/lib/utils'
import { type BudgetStatus, BUDGET_STATUS_COLORS } from '@/types/budget'

export interface BudgetProgressProps {
  /** Percentage used (0-100+) */
  percentUsed: number
  /** Budget status for coloring */
  status: BudgetStatus
  /** Additional CSS classes */
  className?: string
  /** Whether to show the percentage label */
  showLabel?: boolean
  /** Size variant */
  size?: 'sm' | 'md'
}

/**
 * Progress bar for budget usage.
 * Color-coded based on usage percentage.
 * 
 * @example
 * ```tsx
 * <BudgetProgress percentUsed={72} status="warning" />
 * ```
 */
export function BudgetProgress({
  percentUsed,
  status,
  className,
  showLabel = true,
  size = 'md',
}: BudgetProgressProps) {
  const colors = BUDGET_STATUS_COLORS[status]
  const clampedPercent = Math.min(100, Math.max(0, percentUsed))
  
  const heightClass = size === 'sm' ? 'h-1.5' : 'h-2'
  
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('flex-1 rounded-full bg-muted overflow-hidden', heightClass)}>
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            colors.progress,
            status === 'critical' && 'animate-pulse'
          )}
          style={{ width: `${clampedPercent}%` }}
        />
      </div>
      {showLabel && (
        <span className={cn('text-xs font-medium tabular-nums min-w-[3ch]', colors.text)}>
          {Math.round(percentUsed)}%
        </span>
      )}
    </div>
  )
}
